import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { adminService, Client } from '../../services/admin.service';
import { useNotification } from '../../hooks/useNotification';
import ClientCard from '../../components/admin/ClientCard';

const AdminClientsPage: React.FC = () => {
  // Estado de datos
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estado de filtros
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'ACTIVE' | 'SUSPENDED' | 'INACTIVE'>('all');

  // Estado de paginación
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalClients, setTotalClients] = useState(0);

  // Estado de auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 segundos

  // Notificaciones
  const { success: showSuccess, error: showError } = useNotification();

  /**
   * Carga la lista de clientes desde el API
   */
  const loadClients = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = {
        limit: pageSize,
        offset: currentPage * pageSize,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      };

      const response = await adminService.getClients(params);

      if ('error' in response && response.error) {
        setError(response.error);
        showError(`Error al cargar clientes: ${response.error}`);
        setClients([]);
        setTotalClients(0);
      } else {
        setClients(response.clients || []);
        setTotalClients(response.total || 0);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      showError(`Error al cargar clientes: ${errorMessage}`);
      setClients([]);
    } finally {
      setIsLoading(false);
    }
  }, [pageSize, currentPage, statusFilter, showError]);

  /**
   * Efecto para cargar clientes al montar o cambiar filtros
   */
  useEffect(() => {
    loadClients();
  }, [loadClients]);

  /**
   * Efecto para auto-refresh
   */
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadClients();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, loadClients]);

  /**
   * Maneja el refresh manual
   */
  const handleRefresh = useCallback(() => {
    setCurrentPage(0); // Vuelve a la primera página
    loadClients();
    showSuccess('Clientes actualizados');
  }, [loadClients, showSuccess]);

  /**
   * Filtra clientes por búsqueda
   */
  const filteredClients = clients.filter((client) =>
    client.tenantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /**
   * Navega a la página anterior
   */
  const handlePreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  }, []);

  /**
   * Navega a la página siguiente
   */
  const handleNextPage = useCallback(() => {
    const maxPage = Math.ceil(totalClients / pageSize) - 1;
    setCurrentPage((prev) => Math.min(maxPage, prev + 1));
  }, [totalClients, pageSize]);

  /**
   * Cambia el tamaño de página
   */
  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0); // Vuelve a la primera página
  }, []);

  /**
   * Calcula el índice de paginación
   */
  const startIndex = currentPage * pageSize + 1;
  const endIndex = Math.min((currentPage + 1) * pageSize, totalClients);
  const totalPages = Math.ceil(totalClients / pageSize);
  const hasNextPage = currentPage < totalPages - 1;
  const hasPreviousPage = currentPage > 0;

  if (error && isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          <p className="mt-4 text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="py-6 sm:py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Clientes</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Gestiona todos los clientes de la plataforma
              </p>
            </div>
            <div className="flex gap-3">
              {/* Auto Refresh Toggle */}
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                  autoRefresh
                    ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
                title={autoRefresh ? `Auto-refresh cada ${refreshInterval / 1000}s` : 'Auto-refresh desactivado'}
              >
                <ReloadOutlined className={autoRefresh ? 'animate-spin' : ''} />
                {autoRefresh ? 'Auto' : 'Manual'}
              </button>

              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors duration-200"
              >
                <ReloadOutlined className={isLoading ? 'animate-spin' : ''} />
                Actualizar
              </button>
            </div>
          </div>

          {/* Filters Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Search Bar */}
            <div className="relative">
              <SearchOutlined className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'all' | 'ACTIVE' | 'SUSPENDED' | 'INACTIVE');
                setCurrentPage(0);
              }}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos los estados</option>
              <option value="ACTIVE">Activos</option>
              <option value="SUSPENDED">Suspendidos</option>
              <option value="INACTIVE">Inactivos</option>
            </select>
          </div>

          {/* Error Message */}
          {error && !isLoading && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {isLoading && clients.length === 0 && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Cargando clientes...</p>
            </div>
          )}

          {/* Clients Grid (Card View) */}
          {!isLoading && filteredClients.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {filteredClients.map((client) => (
                <Link key={client.tenantId} to={`/admin/clients/${client.tenantId}`}>
                  <ClientCard
                    email={client.email}
                    plan={client.status}
                    status={client.status as 'active' | 'suspended' | 'trial'}
                    mrr={client.totalCost || 0}
                    balance={-(client.deuda || 0)}
                    serviceCount={0}
                  />
                </Link>
              ))}
            </div>
          )}

          {/* Alternative: Table View */}
          {!isLoading && filteredClients.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden mb-6">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Costo Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Deuda
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Creado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredClients.map((client) => (
                    <tr key={client.tenantId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {client.tenantName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {client.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          client.status === 'ACTIVE'
                            ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                            : client.status === 'SUSPENDED'
                            ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                        }`}>
                          {client.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        ${(client.totalCost || 0).toFixed(2)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                        (client.deuda || 0) > 0
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        ${(client.deuda || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {new Date(client.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <Link
                          to={`/admin/clients/${client.tenantId}`}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                        >
                          Ver detalles
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && filteredClients.length === 0 && !error && (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                {searchQuery || statusFilter !== 'all'
                  ? 'No se encontraron clientes con los criterios especificados'
                  : 'No hay clientes disponibles'}
              </p>
            </div>
          )}

          {/* Pagination Footer */}
          {!isLoading && filteredClients.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              {/* Page Size Selector */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 dark:text-gray-400">Mostrar por página:</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
              </div>

              {/* Page Info */}
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Mostrando {startIndex}-{endIndex} de {totalClients} clientes
              </div>

              {/* Navigation Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handlePreviousPage}
                  disabled={!hasPreviousPage}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {currentPage + 1} / {totalPages}
                  </span>
                </div>
                <button
                  onClick={handleNextPage}
                  disabled={!hasNextPage}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminClientsPage;
